import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const LeadMagnetModal = ({ isOpen, onClose }) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const modalRef = useRef(null);
  const previousFocusRef = useRef(null);

  const formEndpoint = import.meta.env.VITE_FORM_ENDPOINT || "https://formsubmit.co/ajax/grandframemedia@gmail.com";

  // Lock body scroll, save previous focus, and add Escape listener
  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement;
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      previousFocusRef.current?.focus();
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Focus trap
  useEffect(() => {
    if (!isOpen || !modalRef.current) return;

    const modal = modalRef.current;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;

      const focusableEls = modal.querySelectorAll(
        'input:not([type="hidden"]):not([style*="display: none"]), button, [tabindex]:not([tabindex="-1"])'
      );
      const firstEl = focusableEls[0];
      const lastEl = focusableEls[focusableEls.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        }
      } else {
        if (document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    };

    modal.addEventListener('keydown', handleKeyDown);

    // Auto-focus the first input
    const firstInput = modal.querySelector('input[name="name"]');
    setTimeout(() => firstInput?.focus(), 100);

    return () => modal.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isSubmitted, onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch(formEndpoint, {
        method: "POST",
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(data)
      });
      
      if (response.ok) {
        setIsSubmitted(true);
        if (window.gtag) {
          window.gtag('event', 'generate_lead', {
            event_category: 'engagement',
            event_label: 'Free Audit Request',
            value: 1,
            lead_type: 'Free Digital Audit'
          });
        }
      } else {
        alert('There was a problem submitting your request. Please try again.');
      }
    } catch (error) {
      console.error(error);
      alert('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="lead-magnet-overlay" onClick={onClose} role="presentation">
          <motion.div 
            ref={modalRef}
            className="lead-magnet-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="lm-title"
            initial={{ opacity: 0, y: 50, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button className="lm-close-btn" onClick={onClose} aria-label="Close" type="button">
              <i className="fa-solid fa-xmark"></i>
            </button>

            {!isSubmitted ? (
              <div className="lm-content">
                <div className="lm-header">
                  <h2 id="lm-title">Is your online presence working for you, or against you?</h2>
                  <p>Get a free Digital Audit. I’ll manually review your social media, SEO, and website speed.</p>
                </div>
                
                <form 
                   className="lm-form" 
                  onSubmit={handleSubmit}
                >
                  <input type="hidden" name="_subject" value="New Free Audit Request!" />
                  <div className="form-group">
                    <input type="text" name="name" placeholder="Full Name" required />
                  </div>
                  <div className="form-group">
                    <input type="text" name="website" placeholder="Business Website URL (optional)" />
                  </div>
                  <div className="form-group">
                    <input type="tel" name="phone" placeholder="WhatsApp / Phone Number" required />
                  </div>
                  
                  {/* Honeypot field for spam prevention */}
                  <input type="text" name="_gotcha" style={{ display: 'none' }} tabIndex={-1} autoComplete="off" />
                  
                  <button type="submit" className="btn btn-primary theme-btn lm-submit" disabled={isSubmitting}>
                    {isSubmitting ? "Sending..." : <>Claim My Free Audit <i className="fa-solid fa-arrow-right"></i></>}
                  </button>
                </form>
              </div>
            ) : (
              <div className="lm-success text-center">
                <i className="fa-solid fa-circle-check" style={{ color: '#10b981', fontSize: '4rem', marginBottom: '1rem' }}></i>
                <h2 id="lm-title">Audit Requested!</h2>
                <p>I will personally review your local presence and reach out via WhatsApp within 24 hours.</p>
                <button className="btn btn-outline" onClick={onClose} style={{ marginTop: '2rem' }} type="button">Close</button>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default LeadMagnetModal;
